"use client";

import { PageLayout } from "@/components/ui/PageLayout";
import { useDemoState } from "./components/use-demo-state";
import { StepProgress } from "./components/StepProgress";
import { Step1ConnectWallet } from "./components/Step1ConnectWallet";
import { Step2DeployAccount } from "./components/Step2DeployAccount";
import { Step3FundAccount } from "./components/Step3FundAccount";
import { Step4SwapTokens } from "./components/Step4SwapTokens";
import { Step5DepositStrategy } from "./components/Step5DepositStrategy";
import { Step6Withdraw } from "./components/Step6Withdraw";
import { Step7ClaimRewards } from "./components/Step7ClaimRewards";
import { BalancePanel } from "./components/BalancePanel";
import { ExecutionLog } from "./components/ExecutionLog";

export default function DemoPage() {
  const state = useDemoState();

  return (
    <PageLayout className="">
      <div className="space-y-6 anim-fadeSlideInUp-500ms">
        <div className="mb-7 anim-fadeSlideInLeft-500ms" style={{ animationDelay: "0.2s" }}>
          <h1 className="text-[28px] font-normal font-serif leading-tight mb-1">Smart Account Walkthrough</h1>
          <p className="text-[11px] tracking-[0.04em]" style={{ color: "var(--text-tertiary)" }}>
            Deploy, fund, swap, and deposit into DeFi pools — all through your on-chain smart account.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left column — Steps */}
          <div className="flex-1">
            <StepProgress stepStatuses={state.stepStatuses} onStepClick={state.goToStep} />
            <div className="space-y-4 mt-4">
              <Step1ConnectWallet
                status={state.stepStatuses.connect}
                isConnected={state.isConnected}
                address={state.address}
              />

              <Step2DeployAccount
                status={state.stepStatuses.deploy}
                isConnected={state.isConnected}
                activeAccountId={state.activeAccountId}
                accountInput={state.accountInput}
                setAccountInput={state.setAccountInput}
                onDeploy={() => void state.deployAccount()}
                onInitialize={() => void state.initializeAccount()}
                onUpgrade={() => void state.upgradeAccount()}
              />

              <Step3FundAccount
                status={state.stepStatuses.fund}
                isConnected={state.isConnected}
                activeAccountId={state.activeAccountId}
                xlmAmount={state.xlmAmount}
                setXlmAmount={state.setXlmAmount}
                swapQuote={state.swapQuote}
                onFund={() => void state.fundAccountWithXLM()}
                onGoBack={() => { state.goToStep("fund"); }}
              />

              <Step4SwapTokens
                status={state.stepStatuses.swap}
                isConnected={state.isConnected}
                activeAccountId={state.activeAccountId}
                xlmAmount={state.xlmAmount}
                swapXlmInput={state.swapXlmInput}
                setSwapXlmInput={state.setSwapXlmInput}
                swapQuote={state.swapQuote}
                onSwap={() => void state.swapXlmToUsdc()}
                onGoBack={() => { state.goToStep("swap"); }}
              />

              <Step5DepositStrategy
                status={state.stepStatuses.deposit}
                isConnected={state.isConnected}
                activeAccountId={state.activeAccountId}
                balances={state.balances}
                onDeposit={() => void state.deposit()}
                onGoBack={() => { state.goToStep("deposit"); }}
              />

              <Step6Withdraw
                status={state.stepStatuses.withdraw}
                isConnected={state.isConnected}
                activeAccountId={state.activeAccountId}
                balances={state.balances}
                onWithdraw={() => void state.withdraw()}
                onGoBack={() => { state.goToStep("withdraw"); }}
              />

              <Step7ClaimRewards
                status={state.stepStatuses.claim}
                isConnected={state.isConnected}
                activeAccountId={state.activeAccountId}
                balances={state.balances}
                onClaim={() => void state.claimRewards()}
                onGoBack={() => { state.goToStep("claim"); }}
              />
            </div>
          </div>

          {/* Right column — Balances + Logs */}
          <div className="lg:w-[38%] lg:sticky lg:top-[84px] lg:self-start space-y-4">
            <BalancePanel
              accountId={state.activeAccountId}
              balances={state.balances}
              onRefresh={() => void state.refreshBalances()}
            />
            <ExecutionLog logs={state.logs} />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
